import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Drawer,
  Input,
  PartnerPicker,
  ProductPicker,
  Select,
  Textarea,
  type PickerValue,
} from '@/components/ui'
import { useCurrentUser } from '@/shell/services/auth'
import { useCreateRma } from '../api/queries'
import type { CreateRmaLineInput, ReasonCode } from '../api/types'
import { ALL_REASON_CODES, REASON_LABELS } from '../utils/stage-meta'

export interface RmaCreateDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (rmaId: string) => void
}

interface DraftLine extends CreateRmaLineInput {
  key: string
}

function newDraftLine(): DraftLine {
  return { key: crypto.randomUUID(), quantity_claimed: '' }
}

/** Drop blank-string fields (untouched inputs default to '') rather than
 *  forwarding them — the backend's Decimal fields (quantity_claimed)
 *  reject '' with a 422; other optional string fields should just be
 *  absent, not an empty string, when the operator left them blank. */
function sanitizeLine(line: CreateRmaLineInput): CreateRmaLineInput {
  const out: CreateRmaLineInput = {}
  for (const [key, value] of Object.entries(line)) {
    if (typeof value === 'string' && value.trim() === '') continue
    ;(out as Record<string, unknown>)[key] = value
  }
  return out
}

export function RmaCreateDrawer({ open, onOpenChange, onCreated }: RmaCreateDrawerProps) {
  const create = useCreateRma()
  const { user } = useCurrentUser()

  const [customer, setCustomer] = useState<PickerValue>(null)
  const [reasonCode, setReasonCode] = useState<ReasonCode>('overship')
  // null = untouched, so a late-resolving useCurrentUser() still backfills
  // the display value without needing an effect + setState.
  const [ownerNameOverride, setOwnerNameOverride] = useState<string | null>(null)
  const [ownerPhone, setOwnerPhone] = useState('')
  const [originalSoName, setOriginalSoName] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([newDraftLine()])

  const ownerName = ownerNameOverride ?? user?.name ?? ''

  function reset() {
    setCustomer(null)
    setReasonCode('overship')
    setOwnerNameOverride(null)
    setOwnerPhone('')
    setOriginalSoName('')
    setNotes('')
    setLines([newDraftLine()])
  }

  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key))
  }

  function submit() {
    if (!ownerName.trim() || !ownerPhone.trim()) {
      toast.error('RMA owner and phone number are required')
      return
    }

    const payloadLines = lines
      .filter((l) => l.product_name || l.quantity_claimed)
      .map(({ key: _key, ...rest }) => sanitizeLine(rest))

    create.mutate(
      {
        reason_code: reasonCode,
        owner_name: ownerName.trim(),
        owner_phone: ownerPhone.trim(),
        customer_id: customer ? customer[0] : null,
        customer_name: customer ? customer[1] : null,
        original_so_name: originalSoName || null,
        notes: notes || null,
        lines: payloadLines,
      },
      {
        onSuccess: (result) => {
          toast.success('RMA created')
          reset()
          onOpenChange(false)
          onCreated(result.rma_id)
        },
        onError: (err) =>
          toast.error(`Create failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      title="New RMA request"
      meta="Return Merchandise Authorization"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={create.isPending} onClick={submit}>
            Create RMA
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Customer</span>
          <PartnerPicker value={customer} onChange={setCustomer} kind="customer" />
        </label>

        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-secondary">RMA owner</span>
            <Input
              value={ownerName}
              onChange={(e) => setOwnerNameOverride(e.target.value)}
              placeholder="Who's handling this return"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-secondary">Phone number</span>
            <Input
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)}
              placeholder="(817) 555-0100"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Reason</span>
          <Select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value as ReasonCode)}
          >
            {ALL_REASON_CODES.map((code) => (
              <option key={code} value={code}>
                {REASON_LABELS[code]}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Original SO # (optional)</span>
          <Input
            value={originalSoName}
            onChange={(e) => setOriginalSoName(e.target.value)}
            placeholder="S00123"
          />
        </label>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-secondary">Lines</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLines((prev) => [...prev, newDraftLine()])}
            >
              <Plus size={12} /> Add line
            </Button>
          </div>
          {lines.map((line) => (
            <div key={line.key} className="flex items-end gap-2 rounded-sm border border-border-subtle p-2">
              <div className="flex-1">
                <ProductPicker
                  value={line.product_id ? [line.product_id, line.product_name ?? ''] : null}
                  onChange={(next) =>
                    updateLine(line.key, {
                      product_id: next ? next[0] : null,
                      product_name: next ? next[1] : null,
                    })
                  }
                  width={220}
                />
              </div>
              <Input
                value={line.quantity_claimed ?? ''}
                onChange={(e) => updateLine(line.key, { quantity_claimed: e.target.value })}
                placeholder="Qty"
                className="w-20"
              />
              <Input
                value={line.uom ?? ''}
                onChange={(e) => updateLine(line.key, { uom: e.target.value })}
                placeholder="UoM"
                className="w-16"
              />
              <Input
                value={line.lot_number ?? ''}
                onChange={(e) => updateLine(line.key, { lot_number: e.target.value })}
                placeholder="Lot #"
                className="w-24"
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Remove line"
                onClick={() => removeLine(line.key)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Notes</span>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Context from the customer's return request…"
          />
        </label>
      </div>
    </Drawer>
  )
}
